import { initializeApp } from "firebase/app";
import * as firebaseauth from "firebase/auth";
import { setAuth, setAuthLoading, setToken } from "store/auth.store";
import { config } from "./config";
import { shareAuth } from "./plugin";
import { reportMessage } from "./sentry";

// NOTE: do not import `firebaseui` or `firebase/compat/*` here.
// `utils/axios` imports this module and ~193 files import `utils/axios`, so
// anything evaluated at module scope in this file lands in the entry chunk of
// every page. `firebaseui` was previously constructed here (~68KB gzipped plus
// its stylesheet) and shipped to signed-in users who never see an auth widget;
// `firebase/compat/app` additionally defeats the modular SDK's tree-shaking.
// Neither had a single importer. If a firebaseui-driven screen is ever needed,
// load it with a dynamic `import()` from that screen, not from here.

const firebaseApp = initializeApp(config.firebase);
const auth = firebaseauth.getAuth(firebaseApp);

// Registers the firebase -> auth-store sync listeners. Invoked once from the app
// entry point (index.tsx) instead of at module-eval time. Calling the auth-store
// setters during module evaluation triggers a circular-import TDZ
// (firebase <-> store/auth.store <-> utils/axios + utils/chatbase): when import
// order places auth.store first in the cycle, `setAuthLoading` is still in its
// temporal dead zone here ("Cannot access 'setAuthLoading' before initialization").
let authListenersInitialized = false;
function initFirebaseAuthListeners() {
	if (authListenersInitialized) return;
	authListenersInitialized = true;

	// Wait for firebase to initialize
	setAuthLoading(true);
	// Firebase's startup can reject (it threw "Database is closing/hidden" when
	// the tab was hidden mid-startup, Sentry LANYAP-WEB-26W). Clear the flag
	// either way so the app never waits on it forever.
	auth
		.authStateReady()
		.catch((error: unknown) =>
			reportMessage("Firebase auth startup failed", { error: String(error) }),
		)
		.finally(() => setAuthLoading(false));

	firebaseauth.onIdTokenChanged(auth, (user) => {
		user ? setAuth(user.uid) : setAuth(null);
		user?.getIdToken().then((token) => {
			setToken(token);
			shareAuth(token ? { token } : null);
		});
	});

	firebaseauth.onAuthStateChanged(auth, (user) => {
		user ? setAuth(user.uid) : setAuth(null);
		user?.getIdToken().then((token) => {
			setToken(token);
			shareAuth(token ? { token } : null);
		});
	});
}

// Get the user's JWT token
const getIdToken = (forceRefresh = false) =>
	auth.authStateReady().then(() => auth.currentUser?.getIdToken(forceRefresh));
const isEmailVerified = () =>
	auth.authStateReady().then(() => auth.currentUser?.emailVerified);
// Sign out the user
const logout = () => firebaseauth.signOut(auth);
// Sign in functions
const signInWithPassword = (email: string, pass: string) =>
	firebaseauth.signInWithEmailAndPassword(auth, email, pass);
const signInWithGoogle = () =>
	firebaseauth.signInWithPopup(auth, new firebaseauth.GoogleAuthProvider());
const signUpWithPassword = (email: string, pass: string) =>
	firebaseauth.createUserWithEmailAndPassword(auth, email, pass);
/**
 * Positive proof that this credential just created a Firebase account, rather
 * than signing an existing one in.
 *
 * The test is deliberately `=== true`, not `!== false`. `getAdditionalUserInfo`
 * returns `AdditionalUserInfo | null`, and `null?.isNewUser` is `undefined` —
 * which is not `=== false`, so a `!== false` test would answer "new" for a null
 * result. Both callers need the safe direction to be "not provably new":
 * `deleteUnprovisionedUser` must not delete a real account, and the signup
 * page must not count an existing user signing in as an account creation.
 */
const isNewFirebaseAccount = (credential: firebaseauth.UserCredential) =>
	firebaseauth.getAdditionalUserInfo(credential)?.isNewUser === true;
/**
 * Roll back a Firebase account that was just created client-side but never got
 * a backing `users` row, i.e. `POST /auth/user` failed or never arrived
 * (throttled — the whole AuthController is 5/60s — network drop, tab closed).
 *
 * `createAuthUser` compensates for its own failures by deleting the Firebase
 * user, but only once the request reaches it. When it doesn't, the account
 * survives as a permanent dead end: it authenticates fine, every API call 401s
 * in AuthMiddleware with "User not found", and the user cannot even sign up
 * again because Firebase answers `auth/email-already-in-use`.
 *
 * Only ever deletes an account this sign-in just created. On the signup page's
 * Google button an EXISTING user can sign in, and deleting their account
 * because of a transient API failure would destroy a real one.
 *
 * The bail-out requires positive proof that the account is new — see
 * `isNewFirebaseAccount` for why anything else (false, null, missing field)
 * bails rather than falling through to a destructive call.
 */
const deleteUnprovisionedUser = async (
	credential: firebaseauth.UserCredential,
) => {
	if (!isNewFirebaseAccount(credential)) return false;
	try {
		await credential.user.delete();
		return true;
	} catch {
		// Deletion needs a recent login; if it somehow fails, at least don't leave
		// a signed-in session that will 401 on every screen.
		await firebaseauth.signOut(auth);
		return false;
	}
};
const forgotPassword = (email: string) =>
	firebaseauth.sendPasswordResetEmail(auth, email);
// Reset password
const resetPassword = (actionCode: string, newPassword: string) =>
	firebaseauth.confirmPasswordReset(auth, actionCode, newPassword);
// Checks a reset link before we show a form for it. Resolves with the account
// email, rejects when the code is expired, already used, or malformed.
const verifyPasswordResetCode = (actionCode: string) =>
	firebaseauth.verifyPasswordResetCode(auth, actionCode);
// Verify email
const verifyEmail = (actionCode: string) =>
	firebaseauth.applyActionCode(auth, actionCode);
const sendEmailVerification = (user: firebaseauth.User) =>
	firebaseauth.sendEmailVerification(user);
const changePassword = (user: firebaseauth.User, newPassword: string) =>
	firebaseauth.updatePassword(user, newPassword);
const changeEmail = (user: firebaseauth.User, newEmail: string) =>
	firebaseauth.updateEmail(user, newEmail);
const verifyBeforeUpdateEmail = (user: firebaseauth.User, newEmail: string) =>
	firebaseauth.verifyBeforeUpdateEmail(user, newEmail);
// Validate password
const validatePassword = (user: firebaseauth.User, oldPassword: string) => {
	if (!user.email) return Promise.reject("Invalid user");
	const credential = firebaseauth.EmailAuthProvider.credential(
		user.email,
		oldPassword,
	);
	return firebaseauth.reauthenticateWithCredential(user, credential);
};
const getCurrentUser = (): Promise<firebaseauth.User | null> => {
	return auth.authStateReady().then(() => auth.currentUser);
};

export {
	auth,
	changeEmail,
	changePassword,
	deleteUnprovisionedUser,
	forgotPassword,
	getCurrentUser,
	getIdToken,
	initFirebaseAuthListeners,
	isEmailVerified,
	isNewFirebaseAccount,
	logout,
	resetPassword,
	sendEmailVerification,
	signInWithGoogle,
	signInWithPassword,
	signUpWithPassword,
	validatePassword,
	verifyBeforeUpdateEmail,
	verifyEmail,
	verifyPasswordResetCode,
};
